// src/pages/AdvisorDeferredServices.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./AdvisorDeferredServices.css";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function todayLocalISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function dateToLocalISO(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function normalizeDateOnly(value) {
  if (!value) return "";

  if (value instanceof Date) {
    return dateToLocalISO(value);
  }

  const raw = safeString(value).trim();
  if (!raw) return "";

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return dateToLocalISO(parsed);
  }

  return "";
}

function addDaysToLocalISO(baseDate, days) {
  const normalizedBase = normalizeDateOnly(baseDate);
  if (!normalizedBase) return "";

  const d = new Date(`${normalizedBase}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";

  d.setDate(d.getDate() + Number(days || 0));
  return dateToLocalISO(d);
}

function addDaysLocalISO(days) {
  return addDaysToLocalISO(todayLocalISO(), days);
}

function notifyDeferredServicesUpdated() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("flowtrak:deferred-services-updated"));
}

function formatDate(value) {
  const normalized = normalizeDateOnly(value);
  if (!normalized) return "—";

  const d = new Date(`${normalized}T12:00:00`);
  if (Number.isNaN(d.getTime())) return normalized;

  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function money(value) {
  const n = safeNumber(value, 0);
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function normalizeStatus(v) {
  const s = safeString(v).trim().toLowerCase();
  if (s === "followed_up") return "followed_up";
  if (s === "closed") return "closed";
  return "open";
}

function isDue(item) {
  const status = normalizeStatus(item?.status);
  if (status !== "open") return false;

  const due = normalizeDateOnly(item?.followUpDate);
  if (!due) return false;

  return due <= todayLocalISO();
}

function getDueTone(item) {
  const status = normalizeStatus(item?.status);
  if (status === "closed") return "closed";
  if (status === "followed_up") return "done";

  const today = todayLocalISO();
  const due = normalizeDateOnly(item?.followUpDate);

  if (!due) return "neutral";
  if (due < today) return "overdue";
  if (due === today) return "due";
  return "upcoming";
}

function dueLabel(item) {
  const tone = getDueTone(item);
  if (tone === "overdue") return "Overdue";
  if (tone === "due") return "Due Today";
  if (tone === "done") return "Followed Up";
  if (tone === "closed") return "Closed";
  return "Upcoming";
}

function normalizeItem(row) {
  const r = row && typeof row === "object" ? row : {};

  return {
    id: safeString(r.id),
    customerName: safeString(r.customerName || r.customer_name),
    roNumber: safeString(r.roNumber || r.ro_number),
    vehicle: safeString(r.vehicle),
    serviceDeclined: safeString(r.serviceDeclined || r.service_declined),
    quotedPrice: safeNumber(r.quotedPrice ?? r.quoted_price, 0),
    followUpDate: normalizeDateOnly(r.followUpDate || r.follow_up_date),
    notes: safeString(r.notes),
    status: normalizeStatus(r.status),
    createdAt: r.createdAt || r.created_at || null,
    updatedAt: r.updatedAt || r.updated_at || null,
    followedUpAt: r.followedUpAt || r.followed_up_at || null,
    closedAt: r.closedAt || r.closed_at || null,
  };
}

function emptyForm() {
  return {
    customerName: "",
    roNumber: "",
    vehicle: "",
    serviceDeclined: "",
    quotedPrice: "",
    serviceDate: todayLocalISO(),
    followUpDays: 30,
    followUpDate: addDaysLocalISO(30),
    notes: "",
  };
}

function Field({ label, children, required }) {
  return (
    <label className="adfs-field">
      <span>
        {label}
        {required ? <b> *</b> : null}
      </span>
      {children}
    </label>
  );
}

function Pill({ tone = "neutral", children }) {
  return <span className={`adfs-pill adfs-pill--${tone}`}>{children}</span>;
}

function DeferredCard({
  item,
  busy,
  onMarkFollowedUp,
  onClose,
  onCopyMessage,
}) {
  const tone = getDueTone(item);

  return (
    <article className={`adfs-item adfs-item--${tone}`}>
      <div className="adfs-itemTop">
        <div className="adfs-itemMain">
          <div className="adfs-itemTitle">
            <span>{item.customerName || "Unnamed Customer"}</span>
            <Pill tone={tone}>{dueLabel(item)}</Pill>
          </div>

          <div className="adfs-itemSub">
            RO #{item.roNumber || "—"}
            {item.vehicle ? ` · ${item.vehicle}` : ""}
          </div>
        </div>

        <div className="adfs-itemDate">
          <span>Follow up</span>
          <strong>{formatDate(item.followUpDate)}</strong>
        </div>
      </div>

      <div className="adfs-serviceBox">
        <div>
          <span className="adfs-miniLabel">Deferred service</span>
          <strong>{item.serviceDeclined || "—"}</strong>
        </div>
        <div>
          <span className="adfs-miniLabel">Quote</span>
          <strong>{money(item.quotedPrice)}</strong>
        </div>
      </div>

      {item.notes ? <p className="adfs-notes">{item.notes}</p> : null}

      <div className="adfs-itemActions">
        <button
          type="button"
          className="adfs-btn adfs-btn--primary"
          onClick={() => onCopyMessage(item)}
          disabled={busy}
        >
          Copy Message
        </button>

        {item.status === "open" ? (
          <button
            type="button"
            className="adfs-btn"
            onClick={() => onMarkFollowedUp(item)}
            disabled={busy}
          >
            Mark Followed Up
          </button>
        ) : null}

        {item.status !== "closed" ? (
          <button
            type="button"
            className="adfs-btn adfs-btn--quiet"
            onClick={() => onClose(item)}
            disabled={busy}
          >
            Close
          </button>
        ) : null}
      </div>
    </article>
  );
}

export default function AdvisorDeferredServices() {
  const auth = useAuth();

  const apiGet = auth?.apiGet;
  const apiPost = auth?.apiPost;
  const apiPatch = auth?.apiPatch;

  const [items, setItems] = useState([]);
  const [form, setForm] = useState(() => emptyForm());
  const [filter, setFilter] = useState("open");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");

  const counts = useMemo(() => {
    const all = Array.isArray(items) ? items : [];

    return {
      total: all.length,
      due: all.filter((item) => isDue(item)).length,
      open: all.filter((item) => normalizeStatus(item.status) === "open").length,
      followedUp: all.filter((item) => normalizeStatus(item.status) === "followed_up").length,
      closed: all.filter((item) => normalizeStatus(item.status) === "closed").length,
    };
  }, [items]);

  const loadItems = useCallback(async () => {
    if (typeof apiGet !== "function") return;

    setLoading(true);
    setErr("");

    try {
      const params = new URLSearchParams();

      if (filter) params.set("status", filter);
      if (safeString(search).trim()) params.set("q", safeString(search).trim());

      const qs = params.toString();
      const data = await apiGet(
        `/api/advisor/deferred-services${qs ? `?${qs}` : ""}`
      );

      const nextItems = Array.isArray(data?.items)
        ? data.items.map(normalizeItem)
        : [];

      setItems(nextItems);
    } catch (e) {
      setErr(e?.message || "Failed to load deferred services.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [apiGet, filter, search]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const grouped = useMemo(() => {
    const due = [];
    const upcoming = [];
    const completed = [];

    items.forEach((item) => {
      const status = normalizeStatus(item.status);

      if (status === "closed" || status === "followed_up") {
        completed.push(item);
        return;
      }

      if (isDue(item)) {
        due.push(item);
        return;
      }

      upcoming.push(item);
    });

    return { due, upcoming, completed };
  }, [items]);

  const canSubmit = useMemo(() => {
    return (
      !!safeString(form.customerName).trim() &&
      !!safeString(form.roNumber).trim() &&
      !!safeString(form.serviceDeclined).trim() &&
      !!safeString(form.followUpDate).trim() &&
      !saving
    );
  }, [form, saving]);

  function updateForm(key, value) {
    setErr("");
    setNotice("");
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function updateServiceDate(value) {
    const serviceDate = normalizeDateOnly(value);

    setErr("");
    setNotice("");
    setForm((prev) => ({
      ...prev,
      serviceDate,
      followUpDate: addDaysToLocalISO(serviceDate, prev.followUpDays || 30),
    }));
  }

  function updateFollowUpDays(days) {
    const nextDays = safeNumber(days, 30);

    setErr("");
    setNotice("");
    setForm((prev) => ({
      ...prev,
      followUpDays: nextDays,
      followUpDate: addDaysToLocalISO(prev.serviceDate, nextDays),
    }));
  }

  async function handleCreate(e) {
    e.preventDefault();

    if (!canSubmit || typeof apiPost !== "function") return;

    setSaving(true);
    setErr("");
    setNotice("");

    try {
      await apiPost("/api/advisor/deferred-services", {
        customerName: safeString(form.customerName).trim(),
        roNumber: safeString(form.roNumber).trim(),
        vehicle: safeString(form.vehicle).trim(),
        serviceDeclined: safeString(form.serviceDeclined).trim(),
        quotedPrice: safeNumber(form.quotedPrice, 0),
        serviceDate: normalizeDateOnly(form.serviceDate),
        followUpDays: safeNumber(form.followUpDays, 30),
        followUpDate: normalizeDateOnly(form.followUpDate),
        notes: safeString(form.notes).trim(),
      });

      setForm(emptyForm());
      setNotice("Deferred service follow-up added.");
      await loadItems();
      notifyDeferredServicesUpdated();
    } catch (e2) {
      setErr(e2?.message || "Failed to create deferred service follow-up.");
    } finally {
      setSaving(false);
    }
  }

  async function patchItem(item, body, successText) {
    if (!item?.id || typeof apiPatch !== "function") return;

    setBusyId(item.id);
    setErr("");
    setNotice("");

    try {
      await apiPatch(`/api/advisor/deferred-services/${item.id}`, body);
      setNotice(successText || "Updated.");
      await loadItems();
      notifyDeferredServicesUpdated();
    } catch (e) {
      setErr(e?.message || "Failed to update deferred service.");
    } finally {
      setBusyId("");
    }
  }

  async function handleMarkFollowedUp(item) {
    await patchItem(item, { status: "followed_up" }, "Marked as followed up.");
  }

  async function handleClose(item) {
    await patchItem(item, { status: "closed" }, "Deferred service item closed.");
  }

  async function handleCopyMessage(item) {
    if (!item?.id || typeof apiGet !== "function") return;

    setBusyId(item.id);
    setErr("");
    setNotice("");

    try {
      const data = await apiGet(`/api/advisor/deferred-services/${item.id}/message`);
      const msg = safeString(data?.message).trim();

      if (!msg) throw new Error("No suggested message returned.");

      await navigator.clipboard.writeText(msg);
      setNotice("Suggested message copied.");
    } catch (e) {
      setErr(e?.message || "Could not copy suggested message.");
    } finally {
      setBusyId("");
    }
  }

  function renderGroup(title, list, emptyText) {
    return (
      <section className="adfs-listGroup">
        <div className="adfs-listHead">
          <h2>{title}</h2>
          <span>{list.length}</span>
        </div>

        {list.length ? (
          <div className="adfs-list">
            {list.map((item) => (
              <DeferredCard
                key={item.id}
                item={item}
                busy={!!busyId}
                onMarkFollowedUp={handleMarkFollowedUp}
                onClose={handleClose}
                onCopyMessage={handleCopyMessage}
              />
            ))}
          </div>
        ) : (
          <div className="adfs-empty">{emptyText}</div>
        )}
      </section>
    );
  }

  return (
    <div className="adfs-page">
      <div className="adfs-header">
        <div>
          <div className="adfs-eyebrow">Advisor Follow-Up</div>
          <h1>Deferred Services</h1>
          <p>
            Track declined services and get reminded when it is time to follow up
            with your customer.
          </p>
        </div>

        <div className="adfs-headerStats">
          <div>
            <span>Due</span>
            <strong>{loading ? "—" : counts.due}</strong>
          </div>
          <div>
            <span>Open</span>
            <strong>{loading ? "—" : counts.open}</strong>
          </div>
        </div>
      </div>

      {err ? <div className="adfs-alert adfs-alert--error">{err}</div> : null}
      {notice ? <div className="adfs-alert adfs-alert--success">{notice}</div> : null}

      <div className="adfs-layout">
        <section className="adfs-panel adfs-formPanel">
          <div className="adfs-panelHead">
            <div>
              <div className="adfs-panelKicker">Set it and follow up later</div>
              <h2>Add Deferred Service</h2>
            </div>
          </div>

          <form className="adfs-form" onSubmit={handleCreate}>
            <div className="adfs-formGrid">
              <Field label="Customer" required>
                <input
                  value={form.customerName}
                  onChange={(e) => updateForm("customerName", e.target.value)}
                  placeholder="Customer name"
                  disabled={saving}
                />
              </Field>

              <Field label="RO Number" required>
                <input
                  value={form.roNumber}
                  onChange={(e) => updateForm("roNumber", e.target.value)}
                  placeholder="RO number"
                  disabled={saving}
                />
              </Field>

              <Field label="Vehicle">
                <input
                  value={form.vehicle}
                  onChange={(e) => updateForm("vehicle", e.target.value)}
                  placeholder="Year make model"
                  disabled={saving}
                />
              </Field>

              <Field label="Quoted Price">
                <input
                  value={form.quotedPrice}
                  onChange={(e) => updateForm("quotedPrice", e.target.value)}
                  placeholder="0.00"
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={saving}
                />
              </Field>
            </div>

            <Field label="Service Declined" required>
              <input
                value={form.serviceDeclined}
                onChange={(e) => updateForm("serviceDeclined", e.target.value)}
                placeholder="Front brake replacement, tires, alignment, etc."
                disabled={saving}
              />
            </Field>

            <div className="adfs-formGrid adfs-formGrid--follow">
              <Field label="Declined / Service Date" required>
                <input
                  value={form.serviceDate}
                  onChange={(e) => updateServiceDate(e.target.value)}
                  type="date"
                  disabled={saving}
                />
              </Field>

              <div className="adfs-quickDates" aria-label="Follow-up interval">
                <button
                  type="button"
                  onClick={() => updateFollowUpDays(30)}
                  disabled={saving}
                  aria-pressed={Number(form.followUpDays) === 30}
                >
                  30 days
                </button>
                <button
                  type="button"
                  onClick={() => updateFollowUpDays(60)}
                  disabled={saving}
                  aria-pressed={Number(form.followUpDays) === 60}
                >
                  60 days
                </button>
                <button
                  type="button"
                  onClick={() => updateFollowUpDays(90)}
                  disabled={saving}
                  aria-pressed={Number(form.followUpDays) === 90}
                >
                  90 days
                </button>
              </div>

              <div
                style={{
                  gridColumn: "1 / -1",
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: "#64748b",
                }}
              >
                Reminder date: <strong>{formatDate(form.followUpDate)}</strong>. The
                sidebar alert will appear on that date and remain until the item is
                followed up or closed.
              </div>
            </div>

            <Field label="Notes">
              <textarea
                value={form.notes}
                onChange={(e) => updateForm("notes", e.target.value)}
                placeholder="Optional context, objection, timing, or next step."
                disabled={saving}
                rows={3}
              />
            </Field>

            <div className="adfs-formActions">
              <button
                type="submit"
                className="adfs-btn adfs-btn--primary"
                disabled={!canSubmit}
              >
                {saving ? "Adding…" : "Add Follow-Up"}
              </button>

              <button
                type="button"
                className="adfs-btn"
                onClick={() => setForm(emptyForm())}
                disabled={saving}
              >
                Clear
              </button>
            </div>
          </form>
        </section>

        <section className="adfs-panel adfs-workPanel">
          <div className="adfs-toolbar">
            <div>
              <div className="adfs-panelKicker">Personal advisor list</div>
              <h2>Your Deferred Follow-Ups</h2>
            </div>

            <button type="button" className="adfs-btn" onClick={loadItems}>
              Refresh
            </button>
          </div>

          <div className="adfs-filters">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer, RO, vehicle, service, or notes"
            />

            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="open">Open</option>
              <option value="all">All</option>
              <option value="followed_up">Followed Up</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {loading ? (
            <div className="adfs-empty">Loading deferred service follow-ups…</div>
          ) : (
            <>
              {renderGroup(
                "Due Now",
                grouped.due,
                "No deferred services are due right now."
              )}

              {renderGroup(
                "Upcoming",
                grouped.upcoming,
                "No upcoming deferred service follow-ups."
              )}

              {filter !== "open"
                ? renderGroup(
                    "Completed / Closed",
                    grouped.completed,
                    "No completed deferred service follow-ups."
                  )
                : null}
            </>
          )}
        </section>
      </div>
    </div>
  );
}